// Import Libraries
import React, { useEffect, useState, useContext } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';

// Services
import userService from '../../services/userService';
import subscriptionService from '../../services/subscriptionService';

// Components
import UserSubscription from '../../components/UserComponents/subscription';
import UserProfileTab from './userProfileTab';
import PaymentMethods from './paymentMethods';
import BillingHistory from './billingHistory';

// Get Context
import { UserContext } from '../../context/User';

function CustomTabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function Profile({ userDetails = {}, isStaff }) {
  const { user } = useContext(UserContext);
  const [useUser, setUseUser] = useState({});
  // 0 = Profile, 1 = Subscription, 2 = Payment Methods, 3 = Billing
  // Persisted so the active tab survives a full page reload (e.g. after the
  // subscription modal closes and reloads the page to refresh user data).
  const [value, setValue] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('sk_profile_active_tab') || '0');
    } catch {
      return 0;
    }
  });

  // Preloaded subscriptions state
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  // hydrate user first
  useEffect(() => {
    const updateUser = async () => {
      if (userDetails?.id) {
        setUseUser(userDetails);
      } else {
        const fetchedUser = await userService.getUserData();
        setUseUser(fetchedUser || user || {});
      }
    };

    if (!useUser?.id) {
      updateUser();
    }
  }, [userDetails, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // helper to compute coupon pricing
  const applyCouponPricing = (arr = []) => {
    arr.forEach((s) => {
      if (s?.coupon) {
        if (s.coupon.type === 'percent') s.couponValue = s.price * (s.coupon.discount / 100);
        if (s.coupon.type === 'fixed') s.couponValue = s.coupon.discount;
        if (s.couponValue != null) s.couponPrice = Math.max(0, s.price - s.couponValue);
      }
    });
    return arr;
  };

  // preload subscriptions once we have a user id
  const fetchSubscriptions = async (uid) => {
    if (!uid) return;
    try {
      setSubscriptionsLoading(true);
      const subDetail = (await subscriptionService.getUserSubscriptionDetail(uid)) || [];
      applyCouponPricing(subDetail);
      setSubscriptions(subDetail);
      const current = subDetail.find((s) => !s.end_date) || subDetail[0] || null;
      setCurrentSubscription(current);
    } catch (e) {
      console.error('Failed to fetch subscriptions:', e);
      setSubscriptions([]);
      setCurrentSubscription(null);
    } finally {
      setSubscriptionsLoading(false);
    }
  };

  // Fire when user becomes available
  useEffect(() => {
    if (useUser?.id) {
      fetchSubscriptions(useUser.id);
    }
  }, [useUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle tab changes
  const handleTabChange = (_event, newValue) => {
    setValue(newValue);
    try {
      sessionStorage.setItem('sk_profile_active_tab', JSON.stringify(newValue));
    } catch {
      // ignore storage errors (e.g. private browsing)
    }
    // (optional) if you want to "refresh on enter" for subscription tab:
    // if (newValue === 1 && useUser?.id) fetchSubscriptions(useUser.id);
  };

  // Expose a refresh fn to child (after Change Plan / Cancel / PaymentDue, etc.)
  const refreshSubscriptions = () => {
    if (useUser?.id) fetchSubscriptions(useUser.id);
  };

  return (
    <div className="Page">
      {!user?.isStaff ? (
        <div className="bold large">Welcome {`${useUser?.first_name || ''}`}</div>
      ) : null}

      {useUser?.id ? (
        <>
          <Tabs value={value} onChange={handleTabChange} aria-label="Profile navigation tabs">
            <Tab label="Profile" value={0} />
            <Tab label="Subscription" value={1} />
            <Tab label="Payment Methods" value={2} />
            <Tab label="Billing" value={3} />
          </Tabs>

          <CustomTabPanel value={value} index={0}>
            <UserProfileTab useUser={useUser} isStaff={user?.isStaff} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <UserSubscription
              userDetails={useUser}
              // preloaded data
              subscriptions={subscriptions}
              currentSubscription={currentSubscription}
              subscriptionsLoading={subscriptionsLoading}
              onRefresh={refreshSubscriptions}
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <PaymentMethods userDetails={useUser} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={3}>
            <BillingHistory userId={useUser.id} />
          </CustomTabPanel>
        </>
      ) : null}
    </div>
  );
}

export default Profile;
